/**
 * Error Handling Utilities
 * Provides consistent, user-friendly error messages across the application
 */

/**
 * Extract a meaningful error message from an API error response
 * @param {Error} error - The error object from axios/fetch
 * @param {string} defaultMessage - Fallback message if none can be extracted
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (error, defaultMessage = 'An error occurred') => {
  // Check for response data detail (FastAPI standard)
  if (error.response?.data?.detail) {
    return typeof error.response.data.detail === 'string' 
      ? error.response.data.detail 
      : JSON.stringify(error.response.data.detail);
  }
  
  // Check for response data message
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Check for error message
  if (error.message) {
    return error.message;
  }
  
  // Fallback
  return defaultMessage;
};

/**
 * Format error message with status code if available
 * @param {Error} error - The error object
 * @param {string} operation - The operation that failed (e.g., "load menu", "save profile")
 * @returns {string} - Formatted error message
 */
export const formatErrorMessage = (error, operation) => {
  const message = getErrorMessage(error, `Unable to ${operation}`);
  const statusCode = error.response?.status;
  
  // Special handling for common HTTP status codes
  if (statusCode === 401 || statusCode === 403) {
    return `${operation} failed: Session expired. Please log out and log back in.`;
  }
  
  if (statusCode === 404) {
    return `${operation} failed: Resource not found.`;
  }
  
  if (statusCode === 500) {
    return `${operation} failed: Server error. Please try again or contact support if the issue persists.`;
  }
  
  if (statusCode === 429) {
    return `${operation} failed: Too many requests. Please wait a moment and try again.`;
  }
  
  // Network errors
  if (message.includes('Network Error') || message.includes('fetch')) {
    return `${operation} failed: Network connection error. Please check your internet and try again.`;
  }
  
  // Return with status code if available
  return statusCode 
    ? `${operation} failed (${statusCode}): ${message}` 
    : `${operation} failed: ${message}`;
};

/**
 * Show toast error with better formatting
 * @param {Error} error - The error object
 * @param {string} operation - The operation that failed
 * @param {Function} toastFn - The toast.error function
 */
export const showErrorToast = (error, operation, toastFn) => {
  const message = formatErrorMessage(error, operation);
  toastFn(message);
  
  // Log to console for debugging
  console.error(`[${operation}] Error:`, error);
};
